import { Calendar, Check, ChevronDown, Clock, Shield } from "lucide-react";
import WobaLogo from "@/assets/woba.svg";
import { type RankedSpace, type SpaceAdjustment } from "../../ai-mode/types";
import { useProposalT } from "../i18n/ProposalI18nContext";
import { allSpacesMatchLocation, formatUTCDate } from "../utils";

interface HeroSectionProps {
  companyName?: string;
  targetCity?: string;
  proposalValidity?: string;
  contractMonths?: number;
  spaces?: RankedSpace[];
  spaceAdjustments?: SpaceAdjustment[];
  isPartnerMode?: boolean;
}

export function HeroSection({
  companyName,
  targetCity,
  proposalValidity,
  contractMonths,
  spaces = [],
  spaceAdjustments = [],
  isPartnerMode = false,
}: HeroSectionProps) {
  const { t } = useProposalT();

  const formattedDate = proposalValidity
    ? formatUTCDate(Number(proposalValidity))
    : null;

  const lowestPricePerPosition = (() => {
    if (spaceAdjustments.length === 0) return null;
    let min = Infinity;
    for (const adj of spaceAdjustments) {
      const space = spaces.find((s) => s.id === adj.spaceId);
      const capacity = space?.capacity ?? 1;
      if (adj.clientPrice > 0 && capacity > 0) {
        const ppp = adj.clientPrice / capacity;
        if (ppp < min) min = ppp;
      }
    }
    return isFinite(min) ? min : null;
  })();

  const scrollToComparativo = (e: React.MouseEvent) => {
    e.preventDefault();
    const targetId = isPartnerMode ? "espacos-selecionados" : "comparativo";
    document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
  };

  const city = targetCity ?? "";
  const spacesCount = spaces.length;
  const locationMatch = allSpacesMatchLocation(spaces, targetCity);

  const contractTermLabel = t(`hero.contractTerm.${contractMonths ?? 12}`);

  const checkItems = [
    {
      title:
        spacesCount === 1
          ? t("hero.spaceSingular", {
              count: spacesCount > 0 ? spacesCount : "—",
            })
          : t("hero.spacePlural", {
              count: spacesCount > 0 ? spacesCount : "—",
            }),
      desc: locationMatch
        ? t("hero.readyInCity", { city: city })
        : t("hero.readyToOperate"),
      icon: "check",
    },
    {
      title: t("hero.singleContract"),
      desc: t("hero.singleContractDesc"),
      icon: "check",
    },
    {
      title: t("hero.contractTermLabel"),
      desc: contractTermLabel,
      icon: "calendar",
    },
    {
      title: t("hero.bestPrice"),
      desc: t("hero.bestPriceDesc"),
      icon: "shield",
    },
  ];

  return (
    <section
      className="relative px-6 overflow-hidden font-space-grotesk"
      style={{
        background:
          "linear-gradient(135deg, #FFD900 0%, #FFE94D 50%, #FFF3A3 100%)",
        minHeight: "520px",
      }}
    >
      <div
        className="relative z-10 max-w-6xl mx-auto"
        style={{ padding: "48px 0 56px" }}
      >
        {/* Top bar: Woba logo + optional client name + validity */}
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <img src={WobaLogo} alt="Woba" className="h-6 w-auto" />
            {companyName && (
              <>
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    background: "rgba(0,0,0,0.15)",
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#3C2110",
                  }}
                >
                  {companyName}
                </span>
              </>
            )}
          </div>
          {formattedDate && (
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{ background: "rgba(0,0,0,0.08)" }}
            >
              <Clock className="w-3.5 h-3.5 text-[#1A1814]" strokeWidth={2} />
              <span
                className="text-xs font-semibold"
                style={{ color: "#1A1814" }}
              >
                {t("hero.pricesGuaranteedUntil", { date: formattedDate })}
              </span>
            </div>
          )}
        </div>

        {/* Main content */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "48px",
            flexWrap: "wrap",
          }}
        >
          {/* Left side */}
          <div style={{ flex: "1.2", minWidth: "320px" }}>
            {companyName && (
              <p
                className="text-sm font-semibold mb-4"
                style={{
                  color: "#3C2110",
                  opacity: 0.7,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {t("hero.preparedFor", { companyName: companyName! })}
              </p>
            )}
            <h1
              className="font-bold mb-5"
              style={{
                fontSize: "clamp(28px, 4vw, 44px)",
                color: "#1A1814",
                lineHeight: 1.1,
              }}
            >
              {locationMatch
                ? t("hero.titleWithCity", { city })
                : t("hero.titleNoCity")}
            </h1>
            {spacesCount > 0 && (
              <p
                className="text-lg mb-8"
                style={{ color: "#3C2110", opacity: 0.8, maxWidth: "480px" }}
              >
                {spacesCount}{" "}
                {spacesCount === 1
                  ? t("hero.optionSingular")
                  : t("hero.optionPlural")}{" "}
                {t("hero.allInclusive")}
              </p>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              {lowestPricePerPosition != null && (
                <div
                  style={{
                    padding: "10px 20px",
                    borderRadius: "12px",
                    background: "rgba(0,0,0,0.08)",
                  }}
                >
                  <span
                    className="text-xs"
                    style={{ color: "#3C2110", opacity: 0.6 }}
                  >
                    {t("hero.startingFrom")}
                  </span>
                  <p
                    style={{
                      fontSize: "24px",
                      fontWeight: 800,
                      color: "#1A1814",
                      margin: 0,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    R${" "}
                    {lowestPricePerPosition.toLocaleString("pt-BR", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: 500,
                        opacity: 0.6,
                      }}
                    >
                      {t("hero.perPositionMonth")}
                    </span>
                  </p>
                </div>
              )}
              <a
                href="#comparativo"
                onClick={scrollToComparativo}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "14px 28px",
                  borderRadius: "12px",
                  background: "#1A1814",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 700,
                  textDecoration: "none",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                }}
              >
                <ChevronDown className="w-4 h-4" strokeWidth={2} />
                {t("hero.viewSpaces")}
              </a>
            </div>
          </div>

          {/* Right side: checkmark items */}
          <div
            style={{
              flex: "0.8",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              minWidth: "280px",
            }}
          >
            {checkItems.map((item) => (
              <div
                key={item.title}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: "rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {item.icon === "shield" ? (
                    <Shield
                      className="w-[18px] h-[18px] text-[#1A1814]"
                      strokeWidth={2.5}
                    />
                  ) : item.icon === "calendar" ? (
                    <Calendar
                      className="w-[18px] h-[18px] text-[#1A1814]"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <Check
                      className="w-[18px] h-[18px] text-[#1A1814]"
                      strokeWidth={2.5}
                    />
                  )}
                </div>
                <div>
                  <p
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "15px",
                      fontWeight: 700,
                      color: "#1A1814",
                      margin: 0,
                    }}
                  >
                    {item.title}
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#3C2110",
                      opacity: 0.6,
                      margin: "4px 0 0",
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
